import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { localizedName } from '@/utils/localized';
import { StatusBadge } from '@/features/reports';
import { GlassCard } from '@/components/glass';
import type { RecentReport } from '../types';
import type { ReportStatus } from '@/features/reports/types';

interface RecentReportsListProps {
  reports: RecentReport[];
}

export function RecentReportsList({ reports }: RecentReportsListProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  if (reports.length === 0) {
    return (
      <GlassCard title={t('dashboard.recentReports')} padding="none">
        <p className="py-6 text-center text-sm text-slate-500">{t('common.noData')}</p>
      </GlassCard>
    );
  }

  return (
    <GlassCard title={t('dashboard.recentReports')} padding="compact" contentClassName="space-y-2">
      {reports.map((report) => {
        const path = `/reports/${report.id}`;
        const typeName = i18n.language === 'he' ? report.reportTypeName : report.reportTypeNameEn;

        return (
          <button
            key={report.id}
            onClick={() => navigate(path)}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-start transition-colors hover:bg-white/20 cursor-pointer"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <FileText className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-neutral-800">
                  {localizedName(report.hospital, i18n.language)}
                </span>
                <StatusBadge status={report.status as ReportStatus} />
              </div>
              <p className="text-xs text-neutral-500">
                {report.date}
                {typeName && ` · ${typeName}`}
                {report.treatmentType && ` · ${t(`treatments.${report.treatmentType}`, report.treatmentType)}`}
                {report.hours ? ` · ${report.hours}h` : ''}
              </p>
            </div>
            {(report.quantity ?? 1) > 1 && (
              <span className="shrink-0 rounded bg-neutral-100 px-1.5 py-0.5 text-xs font-semibold text-neutral-700">
                x{report.quantity}
              </span>
            )}
          </button>
        );
      })}
    </GlassCard>
  );
}
